import { Panel } from 'components/LightConversation/components/Ticket/Panel';
import { useTranslate } from '@tolgee/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { css, cx } from '@emotion/css';
import { DownOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { useSize } from 'ahooks';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { conv2TradeExternalBuyerId } from 'utils/search';
import { SelectMenu } from './SelectMenu';
import { RelatedSKU } from './RelatedSKU';
import { getTradeItems } from '../utils';
import { getPopupContainer } from 'components/LightConversation/utils/popover';

interface PureRelatedItemsProps {
  canEdit?: boolean;
  relatedTradeId: string;
  platform: General.PlatformUpperCase;
  storeId: string;
  relatedSku: Todo.RelatedSku[];
  externalBuyerId: string;
  ready: boolean;
  onSelect: (item: Conversation.Trade.Product, selected: boolean) => void;
  onQuantityChange: (index: number, quantity: number) => void;
  onClear: () => void;
}

export const PureRelatedItems = (props: PureRelatedItemsProps) => {
  const {
    canEdit,
    relatedTradeId,
    platform,
    storeId,
    relatedSku,
    externalBuyerId,
    ready,
    onSelect,
    onQuantityChange,
    onClear,
  } = props;
  const { t } = useTranslate();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const rect = useSize(ref);

  const popoverContentWidth = rect?.width ?? 0;

  const { data } = useQuery({
    ...queries.orders.list({
      external_trade_ids: relatedTradeId,
      internal_store_id: storeId,
      external_buyer_id: conv2TradeExternalBuyerId(platform, externalBuyerId),
      page_size: 9999,
      page_no: 1,
    }),
    enabled: ready && !!relatedTradeId,
  });

  const trade = useMemo(
    () => data?.tradeList?.find((trade) => trade.externalTradeId === relatedTradeId),
    [data, relatedTradeId],
  );

  const itemList = useMemo(() => getTradeItems(trade), [trade]);

  const handleSelect = useCallback(
    (item: Conversation.Trade.Product, selected: boolean) => {
      onSelect(item, selected);
    },
    [onSelect],
  );

  const handleQuantityChange = useCallback(
    (index: number, quantity: number) => {
      onQuantityChange(index, quantity);
    },
    [onQuantityChange],
  );
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear();
    },
    [onClear],
  );

  let content = null;
  if (canEdit) {
    if (trade && itemList.length > 0) {
      content = (
        <SelectMenu
          width={rect?.width ?? 0}
          dataSource={itemList}
          platform={trade.platform}
          unit={trade.unit}
          relatedSku={relatedSku}
          onSelect={handleSelect}
        />
      );
    } else {
      content = (
        <div
          className="p-2 flex items-center justify-center"
          style={{ width: popoverContentWidth }}
        >
          <div className="text-[#84868C]">{t('暂无商品')}</div>
        </div>
      );
    }
  }

  return (
    <Panel title={t('tab.product')}>
      <Popover
        visible={visible}
        onVisibleChange={setVisible}
        trigger="click"
        content={content}
        arrowContent={null}
        placement="bottom"
        overlayClassName={popCls}
        getPopupContainer={getPopupContainer}
      >
        {relatedSku.length > 0 ? (
          <div ref={ref} className="w-full">
            <RelatedSKU
              canEdit={canEdit}
              data={relatedSku}
              storeId={storeId}
              disabled={!canEdit}
              onQuantityChange={handleQuantityChange}
              platform={platform}
              visible={visible}
              onClear={handleClear}
            />
          </div>
        ) : (
          <div
            ref={ref}
            className={cx(
              'w-full border border-solid border-[#D3D7E0] h-[36px] flex items-center px-3 rounded-sm text-[#B5B9BF] min-w-0',
              canEdit && 'cursor-pointer',
            )}
            onClick={() => canEdit && setVisible(true)}
          >
            <span className="flex-1 truncate">
              {t('department.请选择x', { x: t('tab.product') })}
            </span>
            {canEdit && <DownOutlined className={cx('text-xs', visible && 'rotate-180')} />}
          </div>
        )}
      </Popover>
    </Panel>
  );
};

const popCls = css`
  .ant-popover-placement-bottom,
  .ant-popover-placement-bottomLeft,
  .ant-popover-placement-bottomRight {
    padding: 0;
  }
  .ant-popover-inner-content {
    padding: 0;
  }

  .ant-popover-arrow {
    display: none;
  }
`;
